import { PlusIcon } from 'lucide-react';
import { type ComponentProps, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fuzzyMatch } from '../../../common/functions/fuzzy-match';
import { ComboboxContent, ComboboxEmpty, ComboboxItem, ComboboxList, useComboboxFilteredItems } from './combobox';

export interface CreatableMarker {
  __create: string;
}

export const isCreatable = (item: unknown): item is CreatableMarker => {
  return typeof item === 'object' && item !== null && '__create' in item;
};

export interface QueryProps {
  /**
   * Opts into server-side filtering when supplied with `query` and `onQueryChange`: the preset forwards typing to the caller and trusts `items` as the rendered set.
   * Whether `items` clears between queries is up to the caller: clearing shows the loading state in the empty slot, keeping stale items keeps them visible until the new page arrives.
   * The preset owns what the input displays and does not read this back, so it is the caller's mirror of the last reported query, pass the state you store in `onQueryChange`.
   */
  query?: string;
  /** Receives the typed query. Server-side filtering only kicks in when this is supplied together with `query`; either one alone leaves the preset on local filtering. */
  onQueryChange?: (query: string) => void;
}

/**
 * The base-ui change reasons that mean the user produced the text. Every other reason is base-ui rewriting it.
 * `input-clear` covers deleting the last character: base-ui fires a deselect (`onValueChange(null)`), then may
 * repopulate the input from the *controlled* selection (when the caller ignores the deselect). Treat it as user input
 * so the input stays empty.
 */
const USER_EDIT_REASONS: ReadonlySet<string> = new Set(['input-change', 'input-clear']);

/**
 * Owns the text the user has typed into a combobox, so the presets can keep `inputValue` controlled for their whole lifetime.
 * Base UI latches whether `inputValue` is controlled on the first render, so a preset whose caller adds or drops `query`/`onQueryChange` mid-life must not switch between a string and `undefined`.
 * `typedQuery` is `null` while the user isn't editing, which is the presets' cue to display the selection instead.
 * Base UI would normally do that sync itself, but controlling `inputValue` turns it off.
 */
export const useComboboxInputValue = (onQueryChange: ((query: string) => void) | undefined) => {
  const [typedQuery, setTypedQuery] = useState<string | null>(null);
  const onQueryChangeRef = useRef(onQueryChange);
  onQueryChangeRef.current = onQueryChange;

  const handleInputValueChange = useCallback((next: string, reason: string) => {
    const nextQuery = USER_EDIT_REASONS.has(reason) ? next : null;
    setTypedQuery(nextQuery);
    onQueryChangeRef.current?.(nextQuery ?? '');
  }, []);

  const clearQuery = useCallback(() => {
    setTypedQuery(null);
    onQueryChangeRef.current?.('');
  }, []);

  return { typedQuery, handleInputValueChange, clearQuery };
};

export interface CreatableProps<T> {
  /** When supplied, a synthetic "Create" option is rendered for a non-matching query. Selecting it calls `onCreate(query)` and commits the returned value via `onValueChange`. */
  onCreate?: (query: string) => Promise<T> | T;
  /** Optional renderer for the synthetic create option. Receives the current query. Defaults to `t('combobox-create', { query })`. Inert without `onCreate`. */
  renderCreateOption?: (query: string) => ReactNode;
}

/** How far before the tail the load-more probe fires. Matches the popup's small scroll area. */
const TAIL_MARGIN = '120px';

export const useCreatableHelpers = <T,>(
  items: readonly T[],
  getLabel: (item: T) => string,
  itemToStringLabel: ((item: T) => string) | undefined,
  onCreate: ((query: string) => Promise<T> | T) | undefined,
  effectiveQuery: string
) => {
  const getLabelRef = useRef(getLabel);
  getLabelRef.current = getLabel;
  const itemToStringLabelRef = useRef(itemToStringLabel);
  itemToStringLabelRef.current = itemToStringLabel;

  const itemsForView = useMemo(() => {
    if (!onCreate) {
      return items;
    }

    const trimmed = effectiveQuery.trim();
    if (!trimmed) {
      return items;
    }

    const lowered = trimmed.toLowerCase();
    const exactMatch = items.some((item) => getLabelRef.current(item).trim().toLowerCase() === lowered);
    if (exactMatch) {
      return items;
    }

    return [...items, { __create: trimmed } as unknown as T];
  }, [items, onCreate, effectiveQuery]);

  const localFilter = useCallback(
    (item: T, q: string) => (isCreatable(item) ? true : fuzzyMatch(getLabelRef.current(item), q)),
    []
  );

  const stableItemToStringLabel = useCallback((item: T | null) => {
    if (!item) {
      return '';
    }
    if (isCreatable(item)) {
      return item.__create;
    }
    return itemToStringLabelRef.current ? itemToStringLabelRef.current(item) : getLabelRef.current(item);
  }, []);

  return { itemsForView, localFilter, stableItemToStringLabel };
};

/**
 * Key-based item equality that also handles the synthetic creatable marker, so cloned values (e.g. round-tripped
 * through react-hook-form) still match their item. Shared by the single and multi presets.
 */
export const useCreatableItemEquality = <T,>(getKey: (item: T) => string | number) =>
  useCallback(
    (item: T, v: T) => {
      const itemIsCreatable = isCreatable(item);
      const valueIsCreatable = isCreatable(v);
      if (itemIsCreatable || valueIsCreatable) {
        return itemIsCreatable && valueIsCreatable && item.__create === v.__create;
      }
      return getKey(item) === getKey(v);
    },
    [getKey]
  );

interface ComboboxPresetContentProps<T> extends ComboboxItemListProps<T> {
  /** Anchors the popup to something other than the input, for the chips variant. */
  anchor?: ComponentProps<typeof ComboboxContent>['anchor'];
  emptyMessage?: ReactNode;
  /** True for the initial fetch only, so background refetches and filter-to-empty results keep the real message. */
  showLoadingCopy: boolean;
  /** Extra content below the list, outside the filtered collection (e.g. `ComboboxActions`). */
  children?: ReactNode;
}

/** The popup half of both presets: the empty/loading slot, the option list, and any caller-supplied extras. */
export const ComboboxPresetContent = <T,>({
  anchor,
  emptyMessage,
  showLoadingCopy,
  children,
  ...listProps
}: ComboboxPresetContentProps<T>) => {
  const { t } = useTranslation();

  return (
    <ComboboxContent anchor={anchor}>
      <ComboboxEmpty>{showLoadingCopy ? t('combobox-loading') : (emptyMessage ?? t('combobox-empty'))}</ComboboxEmpty>
      <ComboboxItemList {...listProps} />
      {children}
    </ComboboxContent>
  );
};

interface ComboboxItemListProps<T> {
  items: readonly T[];
  getKey: (item: T) => string | number;
  getLabel: (item: T) => string;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore?: () => void;
  renderCreateOption?: (query: string) => ReactNode;
  isServerFilter: boolean;
}

/**
 * Renders the popup option list. Every option is a real DOM node with `content-visibility: auto`, so the browser
 * skips layout/paint for off-screen rows without the complexity of a virtualizer. Pagination is driven by an
 * `IntersectionObserver` on a tail probe (`useInfiniteScroll`); it stays inert when `hasMore` is false.
 */
const ComboboxItemList = <T,>(props: ComboboxItemListProps<T>) => {
  return props.isServerFilter ? (
    <ComboboxRows
      renderItems={props.items}
      getKey={props.getKey}
      getLabel={props.getLabel}
      isLoading={props.isLoading}
      hasMore={props.hasMore}
      onLoadMore={props.onLoadMore}
      renderCreateOption={props.renderCreateOption}
    />
  ) : (
    <LocallyFilteredRows<T> {...props} />
  );
};

const LocallyFilteredRows = <T,>({
  getKey,
  getLabel,
  isLoading,
  hasMore,
  onLoadMore,
  renderCreateOption,
}: ComboboxItemListProps<T>) => {
  const renderItems = useComboboxFilteredItems<T>();
  return (
    <ComboboxRows
      renderItems={renderItems}
      getKey={getKey}
      getLabel={getLabel}
      isLoading={isLoading}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      renderCreateOption={renderCreateOption}
    />
  );
};

interface ComboboxRowsProps<T> {
  renderItems: readonly T[];
  getKey: (item: T) => string | number;
  getLabel: (item: T) => string;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore?: () => void;
  renderCreateOption?: (query: string) => ReactNode;
}

const ComboboxRows = <T,>({
  renderItems,
  getKey,
  getLabel,
  isLoading,
  hasMore,
  onLoadMore,
  renderCreateOption,
}: ComboboxRowsProps<T>) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const tailRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    const tail = tailRef.current;
    const root = scrollRef.current;
    if (!tail || !root || !hasMore) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isLoadingRef.current) {
          onLoadMoreRef.current?.();
        }
      },
      { root, rootMargin: TAIL_MARGIN }
    );
    observer.observe(tail);
    return () => observer.disconnect();
  }, [hasMore]);

  // Nothing to show and nothing more to fetch: leave the popup to `ComboboxEmpty`. When more pages remain we still
  // render the list, so the tail probe exists and pagination can resume from an empty page (a source may return a
  // `next` cursor alongside zero rows).
  if (renderItems.length === 0 && !hasMore) {
    return null;
  }

  // `content-visibility: auto` lets the browser skip rendering off-screen rows; `contain-intrinsic-size`
  // reserves each row's box (~32px) so the scrollbar stays stable while those rows are unrendered.
  return (
    <ComboboxList ref={scrollRef}>
      {renderItems.map((item) => {
        if (isCreatable(item)) {
          return (
            <ComboboxItem
              key={`__create__${item.__create}`}
              value={item}
              className="[contain-intrinsic-size:auto_2rem] [content-visibility:auto]"
              title={renderCreateOption ? undefined : t('combobox-create', { query: item.__create })}
            >
              {renderCreateOption ? (
                renderCreateOption(item.__create)
              ) : (
                <>
                  <PlusIcon aria-hidden="true" />
                  <span className="truncate">{t('combobox-create', { query: item.__create })}</span>
                </>
              )}
            </ComboboxItem>
          );
        }
        return (
          <ComboboxItem
            key={getKey(item)}
            value={item}
            className="[contain-intrinsic-size:auto_2rem] [content-visibility:auto]"
          >
            {getLabel(item)}
          </ComboboxItem>
        );
      })}
      {hasMore && <div ref={tailRef} aria-hidden="true" className="h-px" />}
      {/* Only while appending to rows already on screen. With no rows the popup is `data-empty`, so `ComboboxEmpty`
          shows its own loading copy and a row here would render a second one. */}
      {isLoading && renderItems.length > 0 && (
        <output aria-live="polite" className="block px-2 py-2 text-center text-muted-foreground text-sm">
          {t('combobox-loading')}
        </output>
      )}
    </ComboboxList>
  );
};
